import { Box, Card, Fade, styled } from '@mui/material';
import { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useSlideout } from '../../../hooks/use-slideout';
import { useViewMode, ViewModeType } from '../../../hooks/use-view-mode';
import { useSlideOutCard } from '../../shared/slide-out-card/provider';
import AlertsSlideout from './alerts-widget';
import { ApptRequestsWidget } from './appt-requests-widget';
import CommunicationsFilterSlideout from './comm-filter-widget';
import DialpadSlideout from './dialpad-widget';
import InboxPreviewFilterSlideout from './filter-widget';
import InboxPreviewSlideout from './inbox-widget';
import MessengerSlideout from './messenger-widget';
import StartSMSSlideout from './messenger-widget/start-sms';
import { MfaCodesSlideout } from './mfa-codes-widget';
import { PatientProfileWidget } from './patient-profile-widget';
import SelectPatientSlideout from './select-patient-widget';
import VoicemailSlideout from './voicemail-widget';

const Container = styled(Card)<{ viewmode: ViewModeType }>((props) => ({
  position: 'relative',
  height: '100%',
  borderTopLeftRadius: 16,
  borderBottomLeftRadius: 16,
  minWidth: 320,
  '.intercom-namespace': {
    left: '-40px !important'
  },
  ...(props.viewmode !== 'Desktop' && {
    position: 'absolute',
    right: 0,
    bottom: 0,
    top: 70,
    zIndex: 10000,
    height: 'calc(100vh - 70px)'
  }),
  ...(props.viewmode === 'Phone' && {
    top: 0,
    height: '100vh'
  })
}));

const Backdrop = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.4)'
});

const ContainerInside = styled(Box)({
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  borderTopLeftRadius: 16,
  borderBottomLeftRadius: 16,
  display: 'flex',
  flexDirection: 'column'
});

function SlideoutCardContainer({ children }: { children: ReactElement }) {
  const { portal } = useSlideOutCard();
  const { viewMode } = useViewMode();

  if (viewMode === 'Desktop' || !portal.current) return children;

  return createPortal(children, portal.current);
}

export default function SlideOut() {
  const {
    isVisibleAlerts,
    isVisiblePatient,
    isVisibleMessenger,
    isVisibleInboxPreview,
    isVisibleInboxPreviewFilter,
    isVisibleCommunications,
    isVisibleCommunicationsFilter,
    isVisibleDialpad,
    isVisibleStartSms,
    isVisibleVoicemail,
    isVisisbleSelectPatient,
    isVisibleMfaCodes,
    isApptRequestsVisible,
    close
  } = useSlideout();
  const { viewMode } = useViewMode();
  const isVisible =
    isVisibleAlerts ||
    isVisiblePatient ||
    isVisibleMessenger ||
    isVisibleInboxPreview ||
    isVisibleInboxPreviewFilter ||
    isVisibleCommunications ||
    isVisibleCommunicationsFilter ||
    isVisibleDialpad ||
    isVisibleStartSms ||
    isVisibleVoicemail ||
    isVisisbleSelectPatient ||
    isVisibleMfaCodes ||
    isApptRequestsVisible;

  return (
    <SlideoutCardContainer>
      <>
        {viewMode !== 'Desktop' && (
          <Fade in={isVisible}>
            <Backdrop onClick={close} />
          </Fade>
        )}
        {isVisible && (
          <Container raised viewmode={viewMode}>
            <ContainerInside>
              <PatientProfileWidget />
              <MessengerSlideout />
              <AlertsSlideout />
              <InboxPreviewSlideout />
              <InboxPreviewFilterSlideout />
              <CommunicationsFilterSlideout />
              <DialpadSlideout />
              <StartSMSSlideout />
              <VoicemailSlideout />
              <SelectPatientSlideout />
              <MfaCodesSlideout />
              <ApptRequestsWidget />
            </ContainerInside>
          </Container>
        )}
      </>
    </SlideoutCardContainer>
  );
}
